/**
 * Procedurally renders a 2-bar drum loop so the KPV is playable the moment it
 * boots — no mic permission, no file needed. Kick on quarters, snare on 2/4,
 * hats on eighths. 8 beats at the current BPM.
 */
export function renderDemoLoop(sampleRate: number, bpm: number): { l: Float32Array; r: Float32Array; beats: number } {
  const beats = 8;
  const spb = (60 / bpm) * sampleRate;
  const total = Math.floor(spb * beats);
  const l = new Float32Array(total);
  const r = new Float32Array(total);

  let seed = 0x2f6e2b1;
  const rand = (): number => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    seed >>>= 0;
    return seed / 0xffffffff - 0.5;
  };

  const addKick = (at: number): void => {
    const dur = Math.floor(0.28 * sampleRate);
    let phase = 0;
    for (let i = 0; i < dur && at + i < total; i++) {
      const t = i / sampleRate;
      const freq = 40 + 80 * Math.exp(-t * 28);
      phase += (2 * Math.PI * freq) / sampleRate;
      const env = Math.exp(-t * 11);
      const s = Math.tanh(Math.sin(phase) * 2.2) * env * 0.85;
      l[at + i]! += s;
      r[at + i]! += s;
    }
  };

  const addSnare = (at: number): void => {
    const dur = Math.floor(0.18 * sampleRate);
    let bp = 0;
    for (let i = 0; i < dur && at + i < total; i++) {
      const t = i / sampleRate;
      const n = rand() * 2;
      bp = bp * 0.82 + n * 0.18; // crude band shaping
      const body = Math.sin(2 * Math.PI * 185 * t) * Math.exp(-t * 30) * 0.5;
      const s = ((n - bp) * 0.6 + body) * Math.exp(-t * 16) * 0.7;
      l[at + i]! += s * 0.95;
      r[at + i]! += s * 1.05;
    }
  };

  const addHat = (at: number, open: boolean): void => {
    const dur = Math.floor((open ? 0.12 : 0.045) * sampleRate);
    let hp = 0;
    let prev = 0;
    for (let i = 0; i < dur && at + i < total; i++) {
      const t = i / sampleRate;
      const n = rand() * 2;
      hp = n - prev + hp * 0.6; // highpass-ish
      prev = n;
      const s = hp * Math.exp(-t * (open ? 28 : 70)) * 0.32;
      l[at + i]! += s * (i % 2 ? 0.9 : 1.1);
      r[at + i]! += s * (i % 2 ? 1.1 : 0.9);
    }
  };

  for (let beat = 0; beat < beats; beat++) {
    const at = Math.floor(beat * spb);
    addKick(at);
    if (beat % 2 === 1) addSnare(at);
    addHat(at, false);
    addHat(Math.floor(at + spb / 2), beat % 4 === 3);
  }
  // extra kick pickup before bar 2
  addKick(Math.floor(3.5 * spb));

  // normalize to -3 dB-ish
  let peak = 0;
  for (let i = 0; i < total; i++) {
    const a = Math.max(Math.abs(l[i]!), Math.abs(r[i]!));
    if (a > peak) peak = a;
  }
  const g = peak > 0 ? 0.7 / peak : 1;
  for (let i = 0; i < total; i++) {
    l[i]! *= g;
    r[i]! *= g;
  }
  return { l, r, beats };
}
